import type { ReactFormExtendedApi } from '@tanstack/react-form';
import type { AvailableAfe } from 'types/api-responses';

export type BulkEditView = 'edit' | 'confirm';

/**
 * Form state for the Bulk Edit Slots panel. All fields are optional; an
 * absent (undefined) value means "leave the slot's existing value as-is" —
 * blank fields are NEVER submitted as a clear/null. Keys match the field
 * names on TractNRI exactly so a future Confirm view can diff with
 * Object.entries(form) against row[k] directly.
 */
export interface BulkEditFormState {
  afeSourceId?: string;
  permitDate?: string;
  spudDate?: string;
  firstProductionDate?: string;
  contingenciesCostsDate?: string;
  facilitiesCostsDate?: string;
  drillingCostsDate?: string;
  completionCostsDate?: string;
  otherCostsDate?: string;
  /**
   * Volume source (INS-11129). Written as a set by the picker, never individually: a type
   * curve carries `volumeSourceHref`, a PDP decline carries `forecastSource` +
   * `dcaScenario`, and both always carry the id and type. The same five fields the inline
   * per-slot volume editor writes, so bulk is that edit applied to N slots.
   *
   * `volumeSourceType` and `forecastSource` are ROUTING fields, not decoration:
   * eval_calculator branches on them (`forecast_status` selects the volume pathway,
   * `forecast_source` triggers DCA generation), so overriding the id alone would leave a
   * slot pointing at a type curve while still priced as a decline.
   */
  volumeSourceId?: string;
  volumeSourceType?: string;
  volumeSourceHref?: string;
  forecastSource?: string;
  dcaScenario?: string;
}

export const emptyBulkEditForm: BulkEditFormState = {};

/**
 * Returns true when at least one field on the form has a non-empty value.
 * Used to gate the Continue button so the user can't proceed with an empty
 * patch.
 *
 * @param {BulkEditFormState} f - The current form state.
 * @return {boolean} True when at least one field has a non-empty value.
 */
export const hasAnyValue = (f: BulkEditFormState): boolean =>
  Object.values(f).some((v) => v !== undefined && v !== '');

/**
 * Strips fields whose value is `undefined` or `''` from the form. The result
 * is the patch that should be submitted: ONLY keys the user explicitly set
 * are included, so blank fields can never clear, null out, or overwrite an
 * existing slot value. Any consumer that posts to the backend must run the
 * form through this helper first.
 *
 * @param {BulkEditFormState} f - The current form state.
 * @return {Partial<BulkEditFormState>} A patch containing only populated fields.
 */
export const toBulkEditPatch = (
  f: BulkEditFormState
): Partial<BulkEditFormState> => {
  const patch: Partial<BulkEditFormState> = {};
  (Object.keys(f) as Array<keyof BulkEditFormState>).forEach((key) => {
    const v = f[key];
    if (v !== undefined && v !== '') {
      patch[key] = v;
    }
  });
  return patch;
};

/**
 * Minimal summary of a selected slot rendered in the panel — the slot id is
 * what we operate on; the AFE well name and AFE source id are shown alongside
 * for human context and are all searchable in the edit view.
 */
export interface SelectedSlotSummary {
  slotId: string;
  afeWellName?: string;
  afeSourceId?: string;
}

export type BulkEditAfeOptionsStatus = 'loading' | 'ready' | 'error' | 'empty';

export interface BulkEditAfeOptions {
  status: BulkEditAfeOptionsStatus;
  items: AvailableAfe[];
  errorMessage?: string;
}

/** TanStack Form instance for the bulk-edit-slots panel. */
export type BulkEditFormInstance = ReactFormExtendedApi<
  BulkEditFormState,
  undefined, // TOnMount
  undefined, // TOnChange
  undefined, // TOnChangeAsync
  undefined, // TOnBlur
  undefined, // TOnBlurAsync
  undefined, // TOnSubmit
  undefined, // TOnSubmitAsync
  undefined, // TOnDynamic
  undefined, // TOnDynamicAsync
  undefined, // TOnServer
  undefined // TSubmitMeta
>;
